import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePasswordResetVerify, usePasswordResetConfirm } from "@api/hooks";
import { showSuccess, showError, FormLayout, FormCard, FormField, Button } from "@common";
import { FORM_IDS, resetPasswordSchema } from "@utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

export default function ResetPassword() {
  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.AUTH_RESET_PASSWORD,
  );

  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const verifyMutation = usePasswordResetVerify();
  const confirmMutation = usePasswordResetConfirm();

  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: { new_password: "", new_password_confirm: "" },
  });

  useEffect(() => {
    async function verifyToken() {
      if (!uid || !token) {
        showError("Invalid reset link");
        setIsVerifying(false);
        return;
      }

      try {
        const data = await verifyMutation.mutateAsync({
          uid,
          token,
        });

        if (data.valid) {
          setIsValidToken(true);
          setUserEmail(data.email);
        } else {
          showError("Invalid or expired reset link");
          setIsValidToken(false);
        }
      } catch (error) {
        showError(getApiErrorMessage(error));
        setIsValidToken(false);
      } finally {
        setIsVerifying(false);
      }
    }

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(values) {
    try {
      await confirmMutation.mutateAsync({
        uid,
        token,
        new_password: values.new_password,
        new_password_confirm: values.new_password_confirm,
      });

      recordSubmitSuccess();

      showSuccess("Password reset successfully! Please login with your new password.");
      nav("/login", { replace: true });
    } catch (error) {
      showError(getApiErrorMessage(error));
    }
  }

  if (isVerifying) {
    return (
      <FormLayout>
        <FormCard title="Reset Password">
          <div className="text-center py-8">
            <div className="text-smiles-lightGray">Verifying reset link...</div>
          </div>
        </FormCard>
      </FormLayout>
    );
  }

  if (!isValidToken) {
    return (
      <FormLayout>
        <FormCard title="Invalid Link">
          <div className="text-center space-y-4">
            <div className="text-smiles-red">
              This password reset link is invalid or has expired.
            </div>
            <div className="text-sm text-smiles-lightGray">
              Password reset links expire after 24 hours.
            </div>
            <div className="pt-4">
              <Link to="/forgot-password">
                <Button variant="primary" className="w-full">
                  Request New Reset Link
                </Button>
              </Link>
            </div>
            <div className="text-center mt-4">
              <Link
                to="/login"
                className="text-palette-indigo-badge font-medium hover:text-palette-indigo-heading"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </FormCard>
      </FormLayout>
    );
  }

  return (
    <FormLayout>
      <FormCard title="Reset Password" onSubmit={handleSubmit(onSubmit)} {...formTrackingProps}>
        <div className="space-y-6">
          <div className="text-sm text-smiles-lightGray mb-4">
            Resetting password for:{" "}
            <span className="font-medium text-smiles-darkGray">{userEmail}</span>
          </div>

          <FormField
            label="New Password"
            name="new_password"
            type="password"
            placeholder="Enter new password"
            error={errors.new_password?.message}
            {...register("new_password")}
            register={register}
          />

          <FormField
            label="Confirm New Password"
            name="new_password_confirm"
            type="password"
            placeholder="Confirm new password"
            error={errors.new_password_confirm?.message}
            {...register("new_password_confirm")}
            register={register}
          />

          <div className="text-xs text-smiles-lightGray">
            Password must be at least 8 characters long.
          </div>

          <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
            {isSubmitting ? "Resetting..." : "Reset Password"}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="text-palette-indigo-badge font-medium hover:text-palette-indigo-heading"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </FormCard>
    </FormLayout>
  );
}
